import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "src/widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SysPwdPopup from "@widgets/commonpopup/SysPwdPopup";
import { showPopupModal } from "@widgets/CommonModal";
import SnResizeBar from "@shared/components/SnResizeBar";
import SearchPanel from "@widgets/SearchPanel";

interface Props {
  Menuprops: menuInfo;
}

// 코드관리
const SN_MAS_SET_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const params = {
        id: "sn_mas_set_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0] || [],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const focusedRowNode = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNode && focusedRowNode.data?.yn_system === "Y") {
        await showDialog("alter", "추가불가! 시스템 코드 입니다.");
        return;
      }
      if (!focusedRowNode) {
        await showDialog("alter", "선택된 행이 없습니다.");
        return;
      }
      gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRowL = (): any => {
    try {
      const focusedRowNode = gridRefs.current["grid1"].current.focusedRowNode();
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_clas: focusedRowNode.data?.cd_clas,
        yn_system: "N",
        yn_use: "Y",
        no_seq_sort: "0",
        yn_dummy01: "N",
        yn_dummy02: "N",
        yn_dummy03: "N",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;

    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const focusedRowNodeL = gridRefs.current["grid2"].current.focusedRowNode();
    if (
      (focusedRowNodeH && focusedRowNodeH.data?.yn_system === "Y") ||
      (focusedRowNodeL && focusedRowNodeL.data?.yn_system === "Y")
    ) {
      await showDialog("alter", "삭제불가! 시스템 코드 입니다.");
      return;
    }
    gridRefs.current["grid2"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid2"].current.stopEditing();

      const changesRow = gridRefs.current["grid2"].current.getChange();

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      if (changesRow.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const saveData = {
        mas_codel: changesRow,
      };
      const ok = await fnSave("SN_MAS_SET_REG_001_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnReset") {
      //전체초기화
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      const modifiedRowsL = gridRefs.current["grid2"].current.getChange();

      if (
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0
      ) {
        await showDialog(
          "alter",
          "수정중인 건을 저장 후 진행해주시기 바랍니다."
        );
        return;
      }

      const confirm = await showDialog(
        "confirm",
        "추가 작성한 내용이 모두 삭제됩니다.\n초기설정으로 전체 초기화 하시겠습니까?"
      );
      if (!confirm) {
        return;
      }

      const component = <SysPwdPopup />;
      const result = await showPopupModal({
        title: "SYS계정 암호를 입력해주세요.",
        children: component,
        width: "390px",
        height: "210px",
        checkData: false,
      });
      if (!result || result === "close") return;

      const params = { cd_firm: userInfo.cd_firm };
      const reset = await callApi("SN_MAS_SET_REG_001_btnReset", params);
      if (reset.status) {
        onSearch();
      }
    }

    if (id == "btnApply") {
      //추가사항적용
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      const modifiedRowsL = gridRefs.current["grid2"].current.getChange();

      if (
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0
      ) {
        await showDialog(
          "alter",
          "수정중인 건을 저장 후 진행해주시기 바랍니다."
        );
        return;
      }

      const confirm = await showDialog(
        "confirm",
        "추가사항을 적용 하시겠습니까?"
      );
      if (!confirm) {
        return;
      }

      const params = { cd_firm: userInfo.cd_firm };
      const result = await callApi("SN_MAS_SET_REG_001_btnApply", params);
      if (result.status) {
        onSearch();
      }
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {},
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "코드",
              isEditable: false,
            }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["cd_clas"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "상세코드",
            }}
            events={grids.grid2.events}
            header={
              <div>
                <SnButton
                  text={"전체초기화"}
                  onClick={() => {
                    onGrid1BtnsClick("btnReset");
                  }}
                />
                <SnButton
                  text={"추가사항적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_MAS_SET_REG_001;
